import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

// Simplified: Only mobile simulation (no tablet)
export type ViewportMode = 'mobile' | null;

interface ViewportSimulatorContextType {
  simulatedViewport: ViewportMode;
  setSimulatedViewport: (mode: ViewportMode) => void;
  isSimulatingViewport: boolean;
}

const ViewportSimulatorContext = createContext<ViewportSimulatorContextType | undefined>(undefined);

interface ViewportSimulatorProviderProps {
  children: ReactNode;
}

export function ViewportSimulatorProvider({ children }: ViewportSimulatorProviderProps) {
  const [simulatedViewport, setSimulatedViewportState] = useState<ViewportMode>(null);

  const setSimulatedViewport = useCallback((mode: ViewportMode) => {
    setSimulatedViewportState(mode);
  }, []);

  const value = useMemo(() => ({
    simulatedViewport,
    setSimulatedViewport,
    isSimulatingViewport: simulatedViewport !== null,
  }), [simulatedViewport, setSimulatedViewport]);

  return (
    <ViewportSimulatorContext.Provider value={value}>
      {children}
    </ViewportSimulatorContext.Provider>
  );
}

export function useViewportSimulator(): ViewportSimulatorContextType {
  const context = useContext(ViewportSimulatorContext);
  if (context === undefined) {
    throw new Error('useViewportSimulator must be used within a ViewportSimulatorProvider');
  }
  return context;
}

// Hook for components that might be outside the provider (optional usage)
export function useViewportSimulatorSafe(): ViewportSimulatorContextType {
  const context = useContext(ViewportSimulatorContext);
  return context ?? { simulatedViewport: null, setSimulatedViewport: () => {}, isSimulatingViewport: false };
}
